import React, { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';
import { Alert, Snackbar } from '@mui/material';

type SnackbarMessage = {
    text: string,
    severity: 'success' | 'info' | 'warning' | 'error'
}

type SnackbarContextType = {
    success: (message: string) => void,
    info: (message: string) => void,
    warning: (message: string) => void,
    error: (message: string) => void,
}

const initialState = {
    success: () => {},
    info: () => {},
    warning: () => {},
    error: () => {},
} as SnackbarContextType

export const SnackbarContext = createContext(initialState);

type State = {
    snacks: SnackbarMessage[],
    curSnack: SnackbarMessage | null,
    open: boolean
}

type Action = {
    type: 'ADD_SNACK',
    payload: SnackbarMessage
} | {
    type: 'CLOSE_CURRENT',
} | {
    type: 'OPEN_NEW'
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'ADD_SNACK':
            return {
                ...state,
                snacks: [...state.snacks, action.payload],
            }
        case 'CLOSE_CURRENT':
            return {
                ...state,
                open: false,
            }
        case 'OPEN_NEW':
            return {
                ...state,
                curSnack: state.snacks[0],
                open: true,
                snacks: state.snacks.slice(1),
            }
    }
}

export function SnackbarProvider(props: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(reducer, { snacks: [], curSnack: null, open: false })

    useEffect(() => {
        if (state.snacks.length > 0 && !state.open) {
            setTimeout(() => dispatch({ type: 'OPEN_NEW' }), 500);
        }
    }, [state.snacks, state.open, dispatch])

    const handleClose = useCallback((event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        dispatch({ type: 'CLOSE_CURRENT' })
    }, [dispatch]);

    const handleExited = useCallback(() => {
        dispatch({ type: 'CLOSE_CURRENT' })
    }, [dispatch]);

    const addSnack = useCallback((snack: SnackbarMessage) => dispatch({ type: 'ADD_SNACK', payload: snack }), [dispatch]);
    const success = useCallback((message: string): void => addSnack({ severity: 'success', text: message }), [addSnack]);
    const info = useCallback((message: string): void => addSnack({ severity: 'info', text: message }), [addSnack]);
    const warning = useCallback((message: string): void => addSnack({ severity: 'warning', text: message }), [addSnack]);
    const error = useCallback((message: string): void => {
        console.error(message);
        addSnack({ severity: 'error', text: message })
    }, [addSnack]);

    const context = useMemo(() => ({
        success,
        info,
        warning,
        error,
    }), [success, info, warning, error]);

    return (
        <>
            <SnackbarContext.Provider value={context}>
                {props.children}
            </SnackbarContext.Provider>
            <Snackbar
                open={state.open}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={6000}
                onClose={handleClose}
                TransitionProps={{ onExited: handleExited }}
            >
                <Alert onClose={handleClose} severity={state.curSnack?.severity} sx={{ width: '100%', whiteSpace: 'pre-line' }}>
                    {state.curSnack?.text}
                </Alert>
            </Snackbar>
        </>
    )
}